import React from "react";
import MarketingShell from "@/components/MarketingShell";
import { currentFullDate } from "@/utils/dates";

export default function Privacy() {
  return (
    <MarketingShell>
      <div className="mkt-legal">
        <h1>Privacy Policy</h1>
        <p className="mkt-legal-date" data-testid="legal-date-privacy">
          Last updated: {currentFullDate()} &nbsp;|&nbsp; The Book Cot
        </p>

        <p>
          The Book Cot ("we", "us", "our") is committed to protecting your personal
          data. This Privacy Policy explains how we collect, use, store, and protect
          your information when you use Viral Book Suite and its associated tools.
        </p>

        <h2>1. Who We Are</h2>
        <p>
          The data controller is The Book Cot, registered in England and Wales. We
          operate Viral Book Suite at viralbooksuite.com and its subdomains. Contact us
          at <a href="mailto:hello@viralbooksuite.com">hello@viralbooksuite.com</a> for
          any data-related enquiries.
        </p>

        <h2>2. Data We Collect</h2>
        <p>We collect the following categories of personal data:</p>
        <ul>
          <li><strong>Account data:</strong> Your name and email address when you create an account or purchase access to our tools</li>
          <li><strong>Payment data:</strong> Billing information processed securely by our payment processor (we do not store card details)</li>
          <li><strong>Usage data:</strong> Information about how you interact with our tools, including inputs and generated outputs, to improve our services</li>
          <li><strong>Newsletter data:</strong> Your email address if you subscribe to the Evergreen Passive Monthly Income newsletter</li>
          <li><strong>Technical data:</strong> IP address, browser type, device type, and cookies for security and analytics purposes</li>
        </ul>

        <h2>3. How We Use Your Data</h2>
        <p>We use your personal data to:</p>
        <ul>
          <li>Provide and maintain access to our tools and services</li>
          <li>Process your payments and manage your subscription or licence</li>
          <li>Send you service-related communications, including receipts and account updates</li>
          <li>Send you the newsletter you subscribed to (you may unsubscribe at any time)</li>
          <li>Improve and develop our tools based on usage patterns</li>
          <li>Comply with our legal obligations</li>
        </ul>

        <h2>4. Legal Basis for Processing (UK GDPR)</h2>
        <p>We process your personal data on the following legal grounds:</p>
        <ul>
          <li><strong>Contract performance:</strong> Processing necessary to fulfil your purchase and provide tool access</li>
          <li><strong>Legitimate interests:</strong> Improving our tools, preventing fraud, and maintaining security</li>
          <li><strong>Consent:</strong> For newsletter marketing communications (you can withdraw consent at any time)</li>
          <li><strong>Legal obligation:</strong> Where required by law</li>
        </ul>

        <h2>5. Data Sharing</h2>
        <p>We do not sell your personal data. We may share your data with:</p>
        <ul>
          <li>Payment processors to handle transactions securely</li>
          <li>Email service providers to deliver newsletters and communications</li>
          <li>Analytics providers to understand tool usage (data is anonymised where possible)</li>
          <li>Law enforcement or regulators where required by law</li>
        </ul>

        <h2>6. Data Retention</h2>
        <p>
          We retain your personal data for as long as your account is active or as
          needed to provide services. If you close your account, we will delete your
          personal data within 90 days, except where we are required to retain it for
          legal or accounting purposes (typically 7 years for financial records).
        </p>

        <h2>7. Your Rights</h2>
        <p>Under UK GDPR, you have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate or incomplete data</li>
          <li>Request deletion of your data ("right to be forgotten")</li>
          <li>Object to or restrict our processing of your data</li>
          <li>Request a portable copy of your data</li>
          <li>Withdraw consent at any time where processing is based on consent</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{" "}
          <a href="mailto:hello@viralbooksuite.com">hello@viralbooksuite.com</a>. You
          also have the right to lodge a complaint with the Information Commissioner's
          Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>.
        </p>

        <h2>8. Cookies</h2>
        <p>
          We use essential cookies to operate our tools and analytical cookies to
          understand usage. You can control non-essential cookies through your browser
          settings. By using our tools, you consent to our use of essential cookies.
        </p>

        <h2>9. International Transfers</h2>
        <p>
          Some of our service providers are located outside the UK. Where we transfer
          your data internationally, we ensure appropriate safeguards are in place,
          such as Standard Contractual Clauses approved by the ICO.
        </p>

        <h2>10. Security</h2>
        <p>
          We implement appropriate technical and organisational measures to protect
          your personal data against unauthorised access, disclosure, alteration, or
          destruction. However, no internet transmission is completely secure and we
          cannot guarantee absolute security.
        </p>

        <h2>11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of
          significant changes by email or by posting a prominent notice on our
          website. Your continued use of our tools after such changes constitutes your
          acceptance.
        </p>

        <h2>12. Contact</h2>
        <p>
          For any privacy-related questions or to exercise your rights, please contact
          us at <a href="mailto:hello@viralbooksuite.com">hello@viralbooksuite.com</a>.
        </p>
      </div>
    </MarketingShell>
  );
}
